import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertTriangle, ArrowLeft, CalendarSearch, CheckCircle2, Edit3, FileUp } from 'lucide-react';
import api from '../../../utils/authUtils';
import {
  Container, BackLink, Header, Title, Subtitle, Card, CardTitle,
  Actions, Button, Message, List, ListItem, Badge, WORKSPACE,
} from './ui';

// Bulk entry point: an admin uploads a program document (agenda, session
// grid, committee list) and every person the AI finds in any role - speaker,
// moderator, panelist, host, committee member - is imported immediately as
// an unqueued speaker draft (see SpeakerProfileService.importOneCandidate).
// Nothing is left as page-local state: reviewing, editing, and starting
// research for each one happens afterwards on the normal Speaker Management
// list/detail pages, at whatever pace the admin wants, instead of forcing a
// single all-or-nothing session here. A name that already matches an
// existing speaker draft, user, or account is skipped rather than creating
// a duplicate draft - a fuzzy, name-only warning (there's no email yet to
// check against), so it can miss real matches or flag near-namesakes. Each
// candidate is imported independently, so one failing doesn't affect any
// other candidate in the same upload.
const SpeakerDetection = () => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [message, setMessage] = useState(null);

  const detect = async e => {
    e.preventDefault();
    if (!file) return;
    setDetecting(true); setMessage(null); setResult(null);
    try {
      const form = new FormData(); form.append('file', file);
      const { data } = await api.post('/api/cms/speaker-profiles/detect', form);
      setResult(data);
      if (!data?.candidates?.length) setMessage({ text: t('speakerMgmt.detect.none', 'No candidates were found in this document.') });
    } catch (err) {
      setMessage({ error: true, text: err.response?.data || t('speakerMgmt.detect.failed', 'Could not detect speakers from this document.') });
    } finally { setDetecting(false); }
  };

  const created = (result?.candidates || []).filter(c => c.outcome === 'CREATED');
  const skipped = (result?.candidates || []).filter(c => c.outcome === 'DUPLICATE');
  const failed = (result?.candidates || []).filter(c => c.outcome === 'FAILED');
  const detectedEvent = result?.event;

  return (
    <Container style={{ maxWidth: '48rem' }}>
      <BackLink to={WORKSPACE}><ArrowLeft size={16} /><FormattedMessage id="speakerMgmt.backList" defaultMessage="Back to speakers" /></BackLink>
      <Header><Title><FormattedMessage id="speakerMgmt.detect.title" defaultMessage="Detect speakers from a program" /></Title></Header>
      <Subtitle><FormattedMessage id="speakerMgmt.detect.subtitle" defaultMessage="Upload a program document (agenda, session grid, committee list). Everyone found is added as a new, not-yet-researched speaker draft for you to review at your own pace." /></Subtitle>
      {message && <Message error={message.error}>{message.text}</Message>}

      <Card>
        <CardTitle><FormattedMessage id="speakerMgmt.detect.upload" defaultMessage="Upload document" /></CardTitle>
        <form onSubmit={detect}>
          <Actions style={{ marginTop: 0, alignItems: 'center' }}>
            <input type="file" accept="application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} />
            <Button disabled={!file || detecting}><FileUp size={16} />{detecting
              ? <FormattedMessage id="speakerMgmt.detect.detecting" defaultMessage="Detecting…" />
              : <FormattedMessage id="speakerMgmt.detect.detect" defaultMessage="Detect speakers" />}</Button>
          </Actions>
        </form>
      </Card>

      {detectedEvent && (detectedEvent.name || detectedEvent.date) && (
        <Card style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
          <CalendarSearch size={18} color="#64748b" />
          <div>
            <FormattedMessage id="speakerMgmt.detect.eventGuess" defaultMessage="Detected event: {name}"
              values={{ name: <strong>{detectedEvent.name || detectedEvent.date}</strong> }} />
            {detectedEvent.name && detectedEvent.date ? ` (${detectedEvent.date})` : ''}
            {detectedEvent.matchedEventId
              ? <> — <FormattedMessage id="speakerMgmt.detect.eventMatched" defaultMessage="matched to {name} in this system" values={{ name: <strong>{detectedEvent.matchedEventName}</strong> }} /></>
              : <> — <FormattedMessage id="speakerMgmt.detect.eventUnmatched" defaultMessage="no matching event found; pick one manually when inviting" /></>}
          </div>
        </Card>
      )}

      {result && (
        <Card>
          <CardTitle>
            <FormattedMessage id="speakerMgmt.detect.imported" defaultMessage="{count, plural, one {# new speaker draft added} other {# new speaker drafts added}}" values={{ count: created.length }} />
          </CardTitle>
          {created.length > 0 && (
            <Actions style={{ marginTop: 0, marginBottom: '1rem' }}>
              <Button as={Link} to={`${WORKSPACE}?status=notQueued`}><Edit3 size={15} /><FormattedMessage id="speakerMgmt.detect.reviewList" defaultMessage="Review them in the speaker list" /></Button>
            </Actions>
          )}
          <List>
            {created.map(c => (
              <ListItem key={c.fullName}>
                <div style={{ flex: 1 }}>{c.fullName}</div>
                <Badge style={{ background: '#dcfce7', color: '#166534', display: 'inline-flex', alignItems: 'center', gap: '.3rem' }}>
                  <CheckCircle2 size={13} /><FormattedMessage id="speakerMgmt.detect.added" defaultMessage="Added" />
                </Badge>
                <Button secondary as={Link} to={`${WORKSPACE}/${c.profileId}`}><FormattedMessage id="speakerMgmt.list.edit" defaultMessage="View & edit" /></Button>
              </ListItem>
            ))}
            {skipped.map(c => (
              <ListItem key={c.fullName}>
                <div style={{ flex: 1 }}>{c.fullName}</div>
                <Badge style={{ background: '#f1f5f9', color: '#475569' }}>
                  <FormattedMessage id="speakerMgmt.detect.skipped" defaultMessage="Possible match: {name}" values={{ name: c.duplicateMatchName }} />
                </Badge>
                {c.duplicateProfileId && (
                  <Button secondary as={Link} to={`${WORKSPACE}/${c.duplicateProfileId}`}><FormattedMessage id="speakerMgmt.list.edit" defaultMessage="View & edit" /></Button>
                )}
              </ListItem>
            ))}
            {failed.map((c, index) => (
              <ListItem key={`${c.fullName}-${index}`}>
                <div style={{ flex: 1 }}>{c.fullName}</div>
                <Badge style={{ background: '#fee2e2', color: '#991b1b', display: 'inline-flex', alignItems: 'center', gap: '.3rem' }}>
                  <AlertTriangle size={13} /><FormattedMessage id="speakerMgmt.detect.candidateFailed" defaultMessage="Could not be added: {reason}" values={{ reason: c.errorMessage }} />
                </Badge>
              </ListItem>
            ))}
          </List>
        </Card>
      )}
    </Container>
  );
};

export default SpeakerDetection;
